import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';

import { Toaster } from '@/components/ui/toaster';
import { attachAuthInterceptor, setAuthHeader } from '@/lib/api';
import { router } from '@/router';
import { useAuthStore } from '@/stores/auth-store';
import { useUIStore } from '@/stores/ui-store';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

function useTheme() {
  const theme = useUIStore((s) => s.theme);
  useEffect(() => {
    const root = document.documentElement;
    const apply = (dark: boolean) => root.classList.toggle('dark', dark);

    if (theme === 'system') {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      apply(mql.matches);
      const listener = (e: MediaQueryListEvent) => apply(e.matches);
      mql.addEventListener('change', listener);
      return () => mql.removeEventListener('change', listener);
    }

    apply(theme === 'dark');
    return undefined;
  }, [theme]);
}

function useAxiosAuth() {
  useEffect(() => {
    const token = useAuthStore.getState().accessToken;
    setAuthHeader(token);
    const detach = attachAuthInterceptor({
      getRefreshToken: () => useAuthStore.getState().refreshToken,
      onRefreshed: (session) => useAuthStore.getState().setSession(session),
      onLogout: () => useAuthStore.getState().clear(),
    });
    return () => {
      detach();
    };
  }, []);
}

export function App() {
  useTheme();
  useAxiosAuth();

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster />
    </QueryClientProvider>
  );
}
